import { ElementType } from 'react';
import { SearchResultItemDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { AccountCircle, AssignmentInd, ContactMail, PermContactCalendar, Person } from '@mui/icons-material';
import { ResultIdentity } from './result-identity.component';
import { ResultItemRow } from './result-item-row.component';
import { PERSON_ROLE_LABEL, SearchResultItemDtoSubType } from '../search.constants';
import { dataString } from '../search.utils';
import { convertHyphenated } from '../../../../../core/util/string-conversion';

const PERSON_SUBTYPE_ICON: Record<string, ElementType> = {
  [SearchResultItemDtoSubType.Account]: AssignmentInd,
  [SearchResultItemDtoSubType.Courier]: ContactMail,
  [SearchResultItemDtoSubType.Customer]: PermContactCalendar,
  [SearchResultItemDtoSubType.Occupant]: AccountCircle
};

function PersonIcon({ subType }: { subType?: string }) {
  const Icon = (subType && PERSON_SUBTYPE_ICON[subType]) || Person;
  return <Icon />;
}

function roleLabel(subType?: string): string {
  if (!subType) {
    return 'User';
  }
  return PERSON_ROLE_LABEL[subType] ?? convertHyphenated(subType, { capitalizeEachWord: true });
}

export function PersonResultItem({
  item,
  href,
  onClick
}: {
  item: SearchResultItemDto;
  href?: string;
  onClick?: () => void;
}) {
  const role = roleLabel(item.subType);
  const projectName = dataString(item.data, 'projectName');
  const groupName = dataString(item.data, 'groupName');

  // Couriers are affiliated with their courier group (e.g. DHL) rather than the project,
  // so the project drops to its own line. Everyone else is affiliated with the project.
  const courierGroup = item.subType === SearchResultItemDtoSubType.Courier ? groupName : undefined;
  const affiliationName = courierGroup ?? projectName;
  const affiliation = affiliationName ? `${role} @ ${affiliationName}` : role;
  const project = courierGroup ? projectName : undefined;

  // Email and phone each get their own line (both optional) — emails can be long, so no shared delimiter.
  const email = dataString(item.data, 'email');
  const mobile = dataString(item.data, 'mobile');

  // An incomplete registration has no name yet, so the label comes back empty. The contact it was
  // found by stands in, as on the Incomplete Couriers page, and leaves the secondary lines.
  const contactAsPrimary = item.label.trim() ? undefined : mobile ? 'mobile' : email ? 'email' : undefined;
  const primary = contactAsPrimary === 'mobile' ? mobile : contactAsPrimary === 'email' ? email : item.label;

  // Group is also meaningful for occupants (e.g. their unit/apartment); for customers it's always
  // "Customers" and accounts have none — so we only surface it for occupants and couriers (above).
  const group = item.subType === SearchResultItemDtoSubType.Occupant && groupName ? `Group ${groupName}` : undefined;

  // Deleted users aren't returned; flag the remaining non-active (e.g. inactive) records.
  const status = dataString(item.data, 'status');
  const tag = status && status !== 'active' ? convertHyphenated(status, { capitalizeEachWord: true }) : undefined;

  // Logo in the trailing column, mirroring the project/retailer identity on delivery/order rows.
  // Prefer the more specific group logo (e.g. a courier company), else the owning project's.
  // Image-only — the group/project *names* are already part of the secondary lines.
  const groupImageURL = dataString(item.data, 'groupImageURL');
  const projectImageURL = dataString(item.data, 'projectImageURL');
  const trailing = groupImageURL ? (
    <ResultIdentity name={groupName} imageURL={groupImageURL} />
  ) : projectImageURL ? (
    <ResultIdentity name={projectName} imageURL={projectImageURL} />
  ) : undefined;

  return (
    <ResultItemRow
      icon={<PersonIcon subType={item.subType} />}
      primary={primary ?? item.label}
      secondaryLines={[
        affiliation,
        project,
        group,
        contactAsPrimary !== 'email' && email,
        contactAsPrimary !== 'mobile' && mobile
      ]}
      tag={tag}
      trailing={trailing}
      href={href}
      onClick={onClick}
    />
  );
}
